import type { TFunction } from 'i18next';

interface DialogButton {
  text: string;
  variant?: string;
  action?: string;
  onPress?: () => void;
}

interface DialogProps {
  title: string;
  message: string;
  buttons?: DialogButton[];
}

export interface ConfirmDiscardChangesArgs {
  isDirty: boolean;
  showDialog: (props: DialogProps) => void;
  t: TFunction;
  onDiscard: () => void;
}

/**
 * Imperative counterpart to `useUnsavedLeaveGuard` for navigation paths that
 * do NOT go through React Navigation's stack (and therefore are not covered by
 * `usePreventRemove`) -- e.g. `navigation.setParams` to switch the active
 * task or version inside the same screen.
 *
 * If the form is clean, the discard action runs immediately. Otherwise the
 * shared "Unsaved Changes" dialog is shown and the action runs only when the
 * user explicitly confirms.
 */
export function confirmDiscardChanges({
  isDirty,
  showDialog,
  t,
  onDiscard,
}: ConfirmDiscardChangesArgs): void {
  if (!isDirty) {
    onDiscard();
    return;
  }
  showDialog({
    title: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.title'),
    message: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.message'),
    buttons: [
      { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
      {
        text: t('lecturer.moduleEditor.moduleEditor.dialogs.unsavedChanges.discard'),
        action: 'negative',
        onPress: onDiscard,
      },
    ],
  });
}
